import {HelpCircle, LogOut, User} from "lucide-react";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";

import {Avatar, AvatarFallback} from "@/components/ui/avatar";
import {Button} from "@/components/ui/button";
import ConfirmationModal from "@/components/ui/confirm-dialog";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover";
import {SidebarMenu, SidebarMenuItem, useSidebar} from "@/components/ui/sidebar";
import {cn} from "@/lib/utils";
import {useCompany} from "@/src/modules/company/hooks";
import {AppVersionInfo} from "@/src/modules/core/components/app-version-info";
import {LanguagePicker} from "@/src/modules/core/components/language-picker";
import {useLocalePickerEnabled} from "@/src/modules/core/hooks/use-locale-picker-enabled";
import {OfficerTeamsAndConditionsViewModal} from "@/src/modules/officer/components/officer-teams-and-conditions-view-modal";
import {shiftsService} from "@/src/modules/shifts/services";

import type {Me} from "../types";

function getDisplayName(me: Me): string {
  const parts = [me.first_name, me.last_name].filter(Boolean);
  return parts.length ? parts.join(" ") : me.user_name;
}

function getSecondaryLine(me: Me): string {
  return me.email ?? me.user_name ?? "";
}

const TRIGGER_BUTTON_CLASS =
  "flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-hidden ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground h-12 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground";

/** Conditions link: show full label via wrap (do not use span:last-child truncate from user trigger). */
const CONDITIONS_LINK_BUTTON_CLASS =
  "flex w-full min-h-12 items-start gap-2 overflow-x-hidden rounded-md p-2 text-left text-sm outline-hidden ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground [&>svg]:mt-0.5 [&>svg]:size-4 [&>svg]:shrink-0 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground";

const MENU_ITEM_CLASS =
  "flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground [&_svg]:size-4 [&_svg]:shrink-0";

type NavUserProps = {
  me: Me | null;
  /** Sidebar footer (default) or compact avatar in app header for resident shell. */
  variant?: "sidebar" | "header";
};

export function NavUser({me, variant = "sidebar"}: NavUserProps) {
  const {t} = useTranslation();
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [showConditionsModal, setShowConditionsModal] = useState(false);
  const navigate = useNavigate();
  const {company} = useCompany();
  const {isMobile, state} = useSidebar();
  const isExpanded = state === "expanded";
  const localePickerEnabled = useLocalePickerEnabled();
  const showLanguagePickerInPopover =
    localePickerEnabled && (variant === "header" || (!isExpanded && !isMobile));

  const displayName = me ? getDisplayName(me) : "";
  const secondaryLine = me ? getSecondaryLine(me) : "";
  const conditionsText = me?.company?.officer_teams_and_conditions?.trim() ?? "";
  const showConditionsButton =
    shiftsService.getIsShiftsEnabledForRole() && conditionsText.length > 0;

  const handleLogoutClick = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLogoutOpen(true);
    setPopoverOpen(false);
  };

  const accountPopover = me ? (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger
        render={
          variant === "header" ? (
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={t("account_menu")}
            />
          ) : (
            <button
              type="button"
              className={TRIGGER_BUTTON_CLASS}
              aria-label={t("account_menu")}
            />
          )
        }
      >
        {variant === "header" ? (
          <User className="size-4 shrink-0 text-primary" aria-hidden />
        ) : (
          <Avatar className="size-4 rounded-lg" size="sm">
            <AvatarFallback className="rounded-md">
              <User className="size-4 text-blue-600" />
            </AvatarFallback>
          </Avatar>
        )}
        {variant === "sidebar" ? (
          <div className="grid flex-1 text-left text-sm leading-tight">
            <span className="truncate font-medium">{displayName}</span>
            <span className="text-muted-foreground truncate text-xs">{secondaryLine}</span>
          </div>
        ) : null}
      </PopoverTrigger>
      <PopoverContent
        className="pointer-events-auto min-w-56 rounded-lg p-1"
        side={variant === "header" ? "bottom" : isMobile ? "top" : "right"}
        align="end"
        sideOffset={4}
        positionerClassName="z-[99999]"
      >
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2 px-1 py-1.5">
            <Avatar className="size-8 rounded-lg" size="sm">
              <AvatarFallback className="rounded-lg">
                <User className="size-4 text-blue-600" />
              </AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{displayName}</span>
              <span className="text-muted-foreground truncate text-xs">{secondaryLine}</span>
            </div>
          </div>
          <div className="bg-border my-1 h-px" />
          {showLanguagePickerInPopover ? (
            <>
              <LanguagePicker className="px-1 py-1.5" />
              <div className="bg-border my-1 h-px" />
            </>
          ) : null}
          <button
            type="button"
            className={cn(
              MENU_ITEM_CLASS,
              "text-destructive hover:bg-destructive/10 hover:text-destructive",
            )}
            onPointerDown={handleLogoutClick}
          >
            <LogOut className="size-4" />
            {t("logout")}
          </button>
          {variant === "header" ? (
            <>
              <div className="bg-border my-1 h-px" />
              <AppVersionInfo
                inline
                companyTimezone={company?.timezone}
                className="px-2 py-1.5"
              />
            </>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  ) : null;

  if (variant === "header") {
    return (
      <>
        {accountPopover}
        <OfficerTeamsAndConditionsViewModal
          open={showConditionsModal}
          onClose={() => setShowConditionsModal(false)}
          content={conditionsText}
        />
        <ConfirmationModal
          open={isLogoutOpen}
          title={t("logout")}
          text={t("logout_confirm")}
          confirmButtonClassName="bg-red-600 text-white hover:bg-red-700"
          confirmButtonLabel={t("logout")}
          onClose={() => setIsLogoutOpen(false)}
          onConfirm={() => {
            setIsLogoutOpen(false);
            navigate("/logout");
          }}
        />
      </>
    );
  }

  return (
    <>
      {showConditionsButton ? (
        <SidebarMenu className="mt-2">
          <SidebarMenuItem>
            <button
              type="button"
              className={CONDITIONS_LINK_BUTTON_CLASS}
              onClick={() => setShowConditionsModal(true)}
            >
              <HelpCircle className="size-4 text-muted-foreground" />
              <span
                className={cn(
                  "min-w-0 flex-1 whitespace-normal break-words leading-snug",
                  !isExpanded && !isMobile && "hidden",
                )}
              >
                {t("officer_conditions")}
              </span>
            </button>
          </SidebarMenuItem>
        </SidebarMenu>
      ) : null}
      {accountPopover ? (
        <SidebarMenu>
          <SidebarMenuItem>{accountPopover}</SidebarMenuItem>
        </SidebarMenu>
      ) : null}
      
      <div className="mt-6">
        <AppVersionInfo
          compact={!(isExpanded || isMobile)}
          companyTimezone={company?.timezone}
        />
      </div>

      <OfficerTeamsAndConditionsViewModal
        open={showConditionsModal}
        onClose={() => setShowConditionsModal(false)}
        content={conditionsText}
      />
      <ConfirmationModal
        open={isLogoutOpen}
        title={t("logout")}
        text={t("logout_confirm")}
        confirmButtonClassName="bg-red-600 text-white hover:bg-red-700"
        confirmButtonLabel={t("logout")}
        onClose={() => setIsLogoutOpen(false)}
        onConfirm={() => {
          setIsLogoutOpen(false);
          navigate("/logout");
        }}
      />
    </>
  );
}
